import type { IChangeLog } from '@gp/components/ChangeLogs';
import TimeChange from '@gp/components/TimeChange';
import { DBG_DETAILS_CUR_MILESTONE_ID, MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';
import { BoolFlag } from '@gp/enums/common';
import MeetingCard from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MeetingCard/index';
import { getMeetingStatusIcon } from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import { getIsTBC } from '@gp/pages/Pipeline/utils';
import type {
  ICalendarTableMilestoneDTO,
  ICalendarTableMilestoneHistoryDTO,
} from '@gp/types/calendar';
import { queryJumpable } from '@gp/utils/common';
import { formatMilestoneDate } from '@gp/utils/milestone';
import type { IBaseProps } from '@home/types/common';
import { useMemoizedFn } from 'ahooks';
import classnames from 'classnames';
import React, { useMemo } from 'react';
import { Bubble } from 'tea-component';
import { history } from 'umi';
import './index.scss';

interface IMilestonesProps extends IBaseProps {
  fullScreenContent: React.RefObject<HTMLDivElement | null>;
  project?: string;
  studio?: string;
  bo?: string;
  data?: ICalendarTableMilestoneDTO[];
}

const getChangeLogs = (histories: ICalendarTableMilestoneHistoryDTO[]): IChangeLog[] => {
  if (!Array.isArray(histories) || !histories.length) return [];
  return histories.map((history) => {
    const {
      date_confirm: dateConfirm,
      date,
      reason,
      modify_personnel: updateUser,
      update_time: updateTime,
    } = history || {};
    return {
      dateConfirm,
      date,
      reason,
      updateUser,
      updateTime,
    };
  });
};

const Milestones: React.FC<IMilestonesProps> = (props) => {
  const { style, className, fullScreenContent, project, studio, bo, data, cvmData = {} } = props;
  const { project_id } = cvmData;

  // 获取卡片是否可以跳转页面
  const isPageSkipable = useMemo<boolean>(() => {
    return !!project_id;
  }, [cvmData]);

  // // 卡片点击跳转
  const handleClick = useMemoizedFn(
    (isJump: boolean, e?: React.MouseEvent<HTMLDivElement, MouseEvent>, milestoneId: string) => {
      if (!queryJumpable(e)) return;
      if (!isPageSkipable) return;
      // 跳转产品详情页
      const filter = encodeURIComponent(
        JSON.stringify({
          projectId: project_id || '',
          project: project || '',
          menuFrom: MENU_ID_MAP.pipeline_dashboard, // 用于标识左侧菜单栏当前选中高亮
          tab: 'milestone',
        }),
      );
      const url = `${SLASH_G_POP}/Pipeline/ProjectList/Detail?filter=${filter}`;
      if (isJump) {
        history.push(url);
      }
      localStorage.setItem(DBG_DETAILS_CUR_MILESTONE_ID, milestoneId);
      return url;
    },
  );

  return (
    <div style={style} className={classnames('dbg-calendar-milestone-box', className)}>
      {data?.map((item) => {
        const {
          date,
          start_date_confirm: dateConfirm,
          milestone,
          is_gr: isGR = BoolFlag.False,
          is_expire: isExpire,
          milestone_history: histories,
          category,
          milestone_id: milestoneId,
        } = item || {};
        const isTBC = getIsTBC(dateConfirm);
        const isCheckIn = category === 'check_in';
        const showHistories = Array.isArray(histories) && histories.length > 0;
        const meetingData = item?.milestone_meetings || [];
        const hasMeetingData = Array.isArray(meetingData) && meetingData?.length > 0;

        return (
          // 开发请增加dbg-calendar-milestone--add类型
          // <LinkJump url={handleClick(false) || ''}>
          <div
            key={milestone}
            onClick={(e) => handleClick(true, e, milestoneId)}
            className={classnames('dbg-calendar-milestone dbg-calendar-milestone--milestone', {
              'dbg-calendar-milestone--gr': isGR === BoolFlag.True,
              'dbg-calendar-milestone--check_in': isCheckIn,
              'dbg-calendar-milestone--other_category': !isCheckIn && isGR !== BoolFlag.True,
              'dbg-calendar-milestone--completed': !!isExpire,
            })}
          >
            <div className="dbg-calendar-milestone-hd">
              <div className="dbg-calendar-milestone-date">{formatMilestoneDate(date, isTBC)}</div>
              <div className="dbg-calendar-milestone-flex">
                <div className="dbg-calendar-milestone-left">
                  <Bubble
                    dark
                    positionFixed={false}
                    // @ts-ignore
                    popupContainer={fullScreenContent?.current}
                    content={
                      <div className="dbg-calendar-milestone-bubble">
                        <div className="dbg-calendar-milestone-bubble-name">
                          {/* {formatDateStr(date)} */}
                          {milestone}
                          {isTBC && <span className="dbg-calendar-milestone-bubble-tbc">TBC</span>}
                        </div>
                        <div className="dbg-calendar-milestone-bubble-project">
                          Project: {project || '-'}
                        </div>
                        <div className="dbg-calendar-milestone-bubble-studio">
                          Studio: {studio || '-'}
                        </div>
                        <div className="dbg-calendar-milestone-bubble-bo">BO: {bo || '-'}</div>
                      </div>
                    }
                  >
                    <span className="dbg-calendar-milestone-text dbg-cursor-pointer">
                      {milestone}
                    </span>
                  </Bubble>
                  {isTBC && <span className="dbg-calendar-milestone-tbc">TBC</span>}
                </div>
                {/* Time Change功能入口 */}
                {showHistories && (
                  <TimeChange
                    // @ts-ignore
                    popupContainer={fullScreenContent?.current}
                    data={getChangeLogs(histories)}
                  />
                )}
              </div>
            </div>
            {hasMeetingData && (
              <div className="dbg-calendar-milestone-bd">
                <div className="dbg-milestone-meeting">
                  {meetingData?.map((meeting: any, index: number) => {
                    const typeText = meeting?.source === 1 ? 'Event on' : 'Meeting on';
                    const isDisabled = meeting?.status === 1;
                    const timeText =
                      meeting?.source === 1
                        ? `${meeting?.start_date}~${meeting?.end_date}`
                        : meeting?.start_date;
                    const titleText = `${typeText} ${isDisabled ? '' : timeText}`;
                    const iconType = meeting?.source === 1 ? 'flag' : 'meeting';
                    const statusValue = getMeetingStatusIcon(meeting?.status);
                    return (
                      <MeetingCard
                        key={index}
                        disabled={isDisabled}
                        iconType={iconType}
                        dateText={titleText}
                        status={statusValue}
                        content={meeting?.name}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          // </LinkJump>
        );
      })}
    </div>
  );
};

export default Milestones;
